import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, ArrowLeft, Inbox, ShieldAlert, Mail, Phone, Calendar, Star, MessageSquareQuote, Plus, Pencil, HardHat } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { photoUrl } from "@/components/Portfolio";
import { ProjectForm } from "@/components/admin/ProjectForm";
import { TextsForm } from "@/components/admin/TextsForm";
import { ServicesPageForm } from "@/components/admin/ServicesPageForm";
import { HomeBlocksForm } from "@/components/admin/HomeBlocksForm";
import { ImageSetting } from "@/components/admin/ImageSetting";
import { FILTERS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const STATUSES = [
    { id: "nuova", label: "Nuova", cls: "bg-[#E75C15]/15 text-[#C24E0F] border-[#E75C15]/40" },
    { id: "letta", label: "Letta", cls: "bg-stone-900/5 text-stone-600 border-stone-900/20" },
    { id: "evasa", label: "Evasa", cls: "bg-emerald-700/10 text-emerald-800 border-emerald-700/30" },
];

const REVIEW_STATUSES = [
    { id: "in_attesa", label: "In Attesa", cls: "bg-[#E75C15]/15 text-[#C24E0F] border-[#E75C15]/40" },
    { id: "approvata", label: "Approvata", cls: "bg-emerald-700/10 text-emerald-800 border-emerald-700/30" },
    { id: "rifiutata", label: "Rifiutata", cls: "bg-[#B8430D]/10 text-[#B8430D] border-[#B8430D]/30" },
];

const fmtDate = (iso) =>
    new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const Stars = ({ n }) => (
    <span className="flex gap-0.5">
        {Array.from({ length: n }).map((_, i) => (
            <Star key={i} className="h-3.5 w-3.5 fill-[#E75C15] text-[#E75C15]" />
        ))}
    </span>
);

export default function Admin() {
    const location = useLocation();
    const [user, setUser] = useState(location.state?.user || null);
    const [authState, setAuthState] = useState(location.state?.user ? "ok" : "checking");
    const [tab, setTab] = useState("richieste");
    const [requests, setRequests] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [projects, setProjects] = useState([]);
    const [projectDialog, setProjectDialog] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [settingsObj, setSettingsObj] = useState(null);
    const [instagram, setInstagram] = useState("");
    const [savingIg, setSavingIg] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const [loadingList, setLoadingList] = useState(false);

    useEffect(() => {
        if (location.state?.user) return;
        (async () => {
            try {
                const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
                setUser(res.data);
                setAuthState("ok");
            } catch {
                setAuthState("no");
            }
        })();
    }, [location.state]);

    useEffect(() => {
        if (authState !== "ok") return;
        setLoadingList(true);
        const url =
            tab === "richieste"
                ? `${API}/contact`
                : tab === "recensioni"
                  ? `${API}/reviews/all`
                  : tab === "portfolio"
                    ? `${API}/projects`
                    : `${API}/settings`;
        axios
            .get(url, { withCredentials: true })
            .then((res) =>
                tab === "richieste"
                    ? setRequests(res.data)
                    : tab === "recensioni"
                      ? setReviews(res.data)
                      : tab === "portfolio"
                        ? setProjects(res.data)
                        : (setSettingsObj(res.data), setInstagram(res.data.instagram_url || ""))
            )
            .catch((e) => {
                if (e.response?.status === 403) setForbidden(true);
                else toast.error("Impossibile caricare i dati.");
            })
            .finally(() => setLoadingList(false));
    }, [authState, tab]);

    const login = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/admin";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch {}
        setUser(null);
        setRequests([]);
        setReviews([]);
        setAuthState("no");
        toast.success("Sessione terminata.");
    };

    const setStatus = async (id, status) => {
        try {
            const res = await axios.patch(`${API}/contact/${id}`, { status }, { withCredentials: true });
            setRequests((rs) => rs.map((r) => (r.id === id ? res.data : r)));
        } catch {
            toast.error("Aggiornamento non riuscito.");
        }
    };

    const removeRequest = async (id) => {
        if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
        try {
            await axios.delete(`${API}/contact/${id}`, { withCredentials: true });
            setRequests((rs) => rs.filter((r) => r.id !== id));
            toast.success("Richiesta eliminata.");
        } catch {
            toast.error("Eliminazione non riuscita.");
        }
    };

    const setReviewStatus = async (id, status) => {
        try {
            const res = await axios.patch(`${API}/reviews/${id}`, { status }, { withCredentials: true });
            setReviews((rs) => rs.map((r) => (r.id === id ? res.data : r)));
            if (status === "approvata") toast.success("Recensione pubblicata sul sito.");
        } catch {
            toast.error("Aggiornamento non riuscito.");
        }
    };

    const removeReview = async (id) => {
        if (!window.confirm("Eliminare definitivamente questa recensione?")) return;
        try {
            await axios.delete(`${API}/reviews/${id}`, { withCredentials: true });
            setReviews((rs) => rs.filter((r) => r.id !== id));
            toast.success("Recensione eliminata.");
        } catch {
            toast.error("Eliminazione non riuscita.");
        }
    };

    const openNewProject = () => {
        setEditingProject(null);
        setProjectDialog(true);
    };

    const openEditProject = (p) => {
        setEditingProject(p);
        setProjectDialog(true);
    };

    const onProjectSaved = (saved) => {
        setProjects((ps) => {
            const exists = ps.some((p) => p.id === saved.id);
            return exists ? ps.map((p) => (p.id === saved.id ? saved : p)) : [saved, ...ps];
        });
    };

    const removeProject = async (id) => {
        if (!window.confirm("Eliminare definitivamente questo cantiere dal portfolio?")) return;
        try {
            await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
            setProjects((ps) => ps.filter((p) => p.id !== id));
            toast.success("Cantiere eliminato.");
        } catch {
            toast.error("Eliminazione non riuscita.");
        }
    };

    const nuove = requests.filter((r) => r.status === "nuova").length;

    const saveInstagram = async () => {
        const url = instagram.trim();
        if (url && !/^https:\/\/(www\.)?instagram\.com\//.test(url)) {
            toast.error("Incolla l'indirizzo completo del profilo, es. https://instagram.com/santaluciaquattro");
            return;
        }
        setSavingIg(true);
        try {
            await axios.put(`${API}/settings/instagram_url`, { value: url }, { withCredentials: true });
            toast.success(url ? "Link Instagram attivo sul sito." : "Link Instagram rimosso.");
        } catch {
            toast.error("Salvataggio non riuscito.");
        } finally {
            setSavingIg(false);
        }
    };
    const inAttesa = reviews.filter((r) => r.status === "in_attesa").length;

    if (authState === "checking") {
        return (
            <div data-testid="admin-loading" className="flex min-h-screen items-center justify-center bg-[#F9F7F2]">
                <Loader2 className="h-8 w-8 animate-spin text-[#C24E0F]" />
            </div>
        );
    }

    if (authState === "no") {
        return (
            <div data-testid="admin-login" className="relative flex min-h-screen items-center justify-center bg-[#1C1917] px-4">
                <div className="grain pointer-events-none absolute inset-0" />
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="relative w-full max-w-md border border-[#E75C15]/30 bg-[#F9F7F2] p-10 text-center"
                >
                    <span className="mx-auto mb-6 block w-fit">
                        <LogoFull />
                    </span>
                    <h1 className="font-display text-3xl font-light text-stone-900">Area Riservata</h1>
                    <p className="mt-3 text-sm leading-relaxed text-stone-600">
                        Accedi con il tuo account Google autorizzato per consultare e gestire le
                        richieste e le recensioni arrivate dal sito.
                    </p>
                    <button
                        data-testid="admin-google-login-button"
                        onClick={login}
                        className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#1C1917] px-6 py-4 text-sm font-medium text-stone-100 transition-colors duration-300 hover:bg-[#B8430D]"
                    >
                        <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                            <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.72 12 .72 7.44.72 3.56 3.36 1.76 7.2l3.66 2.84C6.3 7.32 8.9 5.04 12 5.04z"/>
                            <path fill="#4285F4" d="M23.28 12.26c0-.92-.08-1.6-.26-2.3H12v4.34h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.56 2.76c2.14-1.97 3.67-4.88 3.67-8.59z"/>
                            <path fill="#FBBC05" d="M5.43 14.24a7.1 7.1 0 0 1 0-4.48L1.77 6.92a11.3 11.3 0 0 0 0 10.16l3.66-2.84z"/>
                            <path fill="#34A853" d="M12 23.28c3.04 0 5.6-1 7.46-2.72l-3.56-2.76c-.95.66-2.23 1.12-3.9 1.12-3.1 0-5.7-2.28-6.58-5.2l-3.66 2.84c1.8 3.84 5.68 6.72 10.24 6.72z"/>
                        </svg>
                        Accedi con Google
                    </button>
                    <Link
                        to="/"
                        data-testid="admin-back-home"
                        className="mt-6 inline-flex items-center gap-2 text-xs text-stone-500 transition-colors hover:text-[#B8430D]"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Torna al sito
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F7F2]">
            <header className="border-b border-stone-900/10 bg-[#1C1917] text-stone-100">
                <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-8">
                    <LogoFull dark />
                    <div className="flex items-center gap-4">
                        <div className="hidden text-right sm:block">
                            <div className="text-sm font-medium" data-testid="admin-user-name">{user?.name}</div>
                            <div className="font-mono-alt text-[10px] uppercase tracking-widest text-stone-400" data-testid="admin-user-email">
                                {user?.email}
                            </div>
                        </div>
                        {user?.picture && (
                            <img src={user.picture} alt={user?.name} className="h-9 w-9 rounded-full border border-[#E75C15]/50" referrerPolicy="no-referrer" />
                        )}
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="inline-flex items-center gap-2 rounded-full border border-stone-100/20 px-4 py-2 text-xs transition-colors hover:border-[#B8430D] hover:text-[#E75C15]"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            Esci
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
                <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]">
                            Area Riservata
                        </span>
                        <div className="mt-3 flex gap-2">
                            <button
                                data-testid="admin-tab-richieste"
                                onClick={() => setTab("richieste")}
                                className={`rounded-full border px-5 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                    tab === "richieste"
                                        ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                        : "border-stone-900/20 text-stone-500 hover:border-stone-900/40"
                                }`}
                            >
                                Richieste {nuove > 0 && <span className="ml-1 text-[#E75C15]">({nuove})</span>}
                            </button>
                            <button
                                data-testid="admin-tab-recensioni"
                                onClick={() => setTab("recensioni")}
                                className={`rounded-full border px-5 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                    tab === "recensioni"
                                        ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                        : "border-stone-900/20 text-stone-500 hover:border-stone-900/40"
                                }`}
                            >
                                Recensioni {inAttesa > 0 && <span className="ml-1 text-[#E75C15]">({inAttesa})</span>}
                            </button>
                            <button
                                data-testid="admin-tab-portfolio"
                                onClick={() => setTab("portfolio")}
                                className={`rounded-full border px-5 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                    tab === "portfolio"
                                        ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                        : "border-stone-900/20 text-stone-500 hover:border-stone-900/40"
                                }`}
                            >
                                Portfolio
                            </button>
                            <button
                                data-testid="admin-tab-impostazioni"
                                onClick={() => setTab("impostazioni")}
                                className={`rounded-full border px-5 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                    tab === "impostazioni"
                                        ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                        : "border-stone-900/20 text-stone-500 hover:border-stone-900/40"
                                }`}
                            >
                                Impostazioni
                            </button>
                            <button
                                data-testid="admin-tab-testi"
                                onClick={() => setTab("testi")}
                                className={`rounded-full border px-5 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                    tab === "testi"
                                        ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                        : "border-stone-900/20 text-stone-500 hover:border-stone-900/40"
                                }`}
                            >
                                Testi Home
                            </button>
                            <button
                                data-testid="admin-tab-pagina-servizi"
                                onClick={() => setTab("pagina-servizi")}
                                className={`rounded-full border px-5 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                    tab === "pagina-servizi"
                                        ? "border-[#1C1917] bg-[#1C1917] text-stone-100"
                                        : "border-stone-900/20 text-stone-500 hover:border-stone-900/40"
                                }`}
                            >
                                Pagina Servizi
                            </button>
                        </div>
                    </div>
                    <div className="flex gap-6 font-mono-alt text-xs uppercase tracking-widest text-stone-500">
                        {tab === "richieste" ? (
                            <>
                                <span data-testid="admin-count-total">
                                    Totali: <strong className="text-stone-900">{requests.length}</strong>
                                </span>
                                <span data-testid="admin-count-new">
                                    Nuove: <strong className="text-[#B8430D]">{nuove}</strong>
                                </span>
                            </>
                        ) : tab === "recensioni" ? (
                            <>
                                <span data-testid="admin-reviews-total">
                                    Totali: <strong className="text-stone-900">{reviews.length}</strong>
                                </span>
                                <span data-testid="admin-reviews-pending">
                                    Da moderare: <strong className="text-[#B8430D]">{inAttesa}</strong>
                                </span>
                            </>
                        ) : tab === "portfolio" ? (
                            <span data-testid="admin-projects-total">
                                Cantieri: <strong className="text-stone-900">{projects.length}</strong>
                            </span>
                        ) : null}
                    </div>
                </div>

                {forbidden && (
                    <div data-testid="admin-forbidden" className="mb-6 flex items-center gap-3 border border-[#B8430D]/40 bg-[#B8430D]/10 p-4 text-sm text-[#B8430D]">
                        <ShieldAlert className="h-5 w-5 shrink-0" />
                        Questo account non è autorizzato a gestire i contenuti. Contatta l'amministratore.
                    </div>
                )}

                {loadingList ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-[#C24E0F]" />
                    </div>
                ) : tab === "richieste" ? (
                    requests.length === 0 ? (
                        <div data-testid="admin-empty" className="flex flex-col items-center gap-4 border border-dashed border-stone-900/20 py-20 text-stone-500">
                            <Inbox className="h-10 w-10" />
                            <p className="text-sm">Nessuna richiesta ricevuta finora.</p>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {requests.map((r, i) => (
                                <motion.article
                                    key={r.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                                    data-testid={`admin-request-${r.id}`}
                                    className="border border-stone-900/10 bg-white p-6"
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div>
                                            <h2 className="font-display text-xl font-medium text-stone-900">{r.name}</h2>
                                            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-500">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Mail className="h-3.5 w-3.5 text-[#C24E0F]" />
                                                    {r.email}
                                                </span>
                                                {r.phone && (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Phone className="h-3.5 w-3.5 text-[#C24E0F]" />
                                                        {r.phone}
                                                    </span>
                                                )}
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Calendar className="h-3.5 w-3.5 text-[#C24E0F]" />
                                                    {fmtDate(r.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {STATUSES.map((s) => (
                                                <button
                                                    key={s.id}
                                                    data-testid={`admin-status-${s.id}-${r.id}`}
                                                    onClick={() => setStatus(r.id, s.id)}
                                                    className={`rounded-full border px-3 py-1 font-mono-alt text-[10px] uppercase tracking-wider transition-colors ${
                                                        r.status === s.id ? s.cls : "border-stone-900/10 text-stone-400 hover:border-stone-900/30"
                                                    }`}
                                                >
                                                    {s.label}
                                                </button>
                                            ))}
                                            <button
                                                data-testid={`admin-delete-${r.id}`}
                                                onClick={() => removeRequest(r.id)}
                                                className="ml-1 p-2 text-stone-400 transition-colors hover:text-[#B8430D]"
                                                aria-label="Elimina richiesta"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <span className="border border-[#E75C15]/40 bg-[#E75C15]/10 px-3 py-1 font-mono-alt text-[10px] uppercase tracking-wider text-[#C24E0F]">
                                            {r.property_type}
                                        </span>
                                        {r.budget && (
                                            <span className="border border-stone-900/10 px-3 py-1 font-mono-alt text-[10px] uppercase tracking-wider text-stone-500">
                                                Budget: {r.budget}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-4 border-l-2 border-[#E75C15]/60 pl-4 text-sm leading-relaxed text-stone-600">
                                        {r.message}
                                    </p>
                                </motion.article>
                            ))}
                        </div>
                    )
                ) : tab === "recensioni" ? (
                    reviews.length === 0 ? (
                    <div data-testid="admin-reviews-empty" className="flex flex-col items-center gap-4 border border-dashed border-stone-900/20 py-20 text-stone-500">
                        <MessageSquareQuote className="h-10 w-10" />
                        <p className="text-sm">Nessuna recensione ricevuta finora.</p>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {reviews.map((r, i) => (
                            <motion.article
                                key={r.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                                data-testid={`admin-review-${r.id}`}
                                className="border border-stone-900/10 bg-white p-6"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="font-display text-xl font-medium text-stone-900">{r.name}</h2>
                                            <Stars n={r.rating} />
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-500">
                                            {r.email && (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Mail className="h-3.5 w-3.5 text-[#C24E0F]" />
                                                    {r.email}
                                                </span>
                                            )}
                                            {r.project && <span>{r.project}</span>}
                                            <span className="inline-flex items-center gap-1.5">
                                                <Calendar className="h-3.5 w-3.5 text-[#C24E0F]" />
                                                {fmtDate(r.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {REVIEW_STATUSES.map((s) => (
                                            <button
                                                key={s.id}
                                                data-testid={`admin-review-status-${s.id}-${r.id}`}
                                                onClick={() => setReviewStatus(r.id, s.id)}
                                                className={`rounded-full border px-3 py-1 font-mono-alt text-[10px] uppercase tracking-wider transition-colors ${
                                                    r.status === s.id ? s.cls : "border-stone-900/10 text-stone-400 hover:border-stone-900/30"
                                                }`}
                                            >
                                                {s.label}
                                            </button>
                                        ))}
                                        <button
                                            data-testid={`admin-review-delete-${r.id}`}
                                            onClick={() => removeReview(r.id)}
                                            className="ml-1 p-2 text-stone-400 transition-colors hover:text-[#B8430D]"
                                            aria-label="Elimina recensione"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                <p className="mt-4 border-l-2 border-[#E75C15]/60 pl-4 font-display text-lg italic leading-relaxed text-stone-600">
                                    “{r.text}”
                                </p>
                            </motion.article>
                        ))}
                    </div>
                    )
                ) : tab === "portfolio" ? (
                    <div>
                        <div className="mb-6">
                            <button
                                data-testid="portfolio-new-button"
                                onClick={openNewProject}
                                className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] px-6 py-3 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors hover:bg-[#B8430D]"
                            >
                                <Plus className="h-4 w-4" />
                                Nuovo Cantiere
                            </button>
                        </div>
                        {projects.length === 0 ? (
                            <div data-testid="admin-projects-empty" className="flex flex-col items-center gap-4 border border-dashed border-stone-900/20 py-20 text-stone-500">
                                <HardHat className="h-10 w-10" />
                                <p className="text-sm">Nessun cantiere in portfolio.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                {projects.map((p, i) => (
                                    <motion.article
                                        key={p.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: i * 0.04, ease: EASE }}
                                        data-testid={`admin-project-${p.id}`}
                                        className="flex items-center gap-4 border border-stone-900/10 bg-white p-4"
                                    >
                                        <img
                                            src={photoUrl(p.photos?.[0])}
                                            alt={p.title}
                                            className="h-24 w-24 shrink-0 border border-stone-900/10 object-cover"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <h2 className="truncate font-display text-lg font-medium text-stone-900">{p.title}</h2>
                                            <p className="mt-1 font-mono-alt text-[10px] uppercase tracking-[0.15em] text-stone-500">
                                                {p.location} · {p.year}
                                            </p>
                                            <p className="mt-1 text-xs text-stone-500">
                                                {FILTERS.find((f) => f.id === p.category)?.label} · {p.photos?.length || 0} foto
                                            </p>
                                        </div>
                                        <div className="flex shrink-0 flex-col gap-2">
                                            <button
                                                data-testid={`project-edit-${p.id}`}
                                                onClick={() => openEditProject(p)}
                                                className="p-2 text-stone-400 transition-colors hover:text-[#C24E0F]"
                                                aria-label="Modifica cantiere"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </button>
                                            <button
                                                data-testid={`project-delete-${p.id}`}
                                                onClick={() => removeProject(p.id)}
                                                className="p-2 text-stone-400 transition-colors hover:text-[#B8430D]"
                                                aria-label="Elimina cantiere"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </motion.article>
                                ))}
                            </div>
                        )}
                    </div>
                ) : tab === "impostazioni" ? (
                    <div>
                        <h2 className="font-display text-2xl font-light text-stone-900">Foto del sito</h2>
                        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-stone-600">
                            Sostituisci le immagini principali del sito. Consigliato: foto verticali
                            per la home e per la foto grande del Chi Siamo (almeno 1200×1600 px, max
                            10 MB), quadrata per la foto piccola sovrapposta.
                        </p>
                        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                            <ImageSetting
                                settingKey="hero_image"
                                title="Home — Foto nella cornice ad arco"
                                hint="La prima immagine che vedono i visitatori, in cima alla pagina."
                                value={settingsObj?.hero_image}
                                arch
                                testId="settings-hero"
                            />
                            <ImageSetting
                                settingKey="about_image_main"
                                title="Chi Siamo — Foto grande"
                                hint="La foto verticale grande a sinistra della sezione Chi Siamo."
                                value={settingsObj?.about_image_main}
                                testId="settings-about-main"
                            />
                            <ImageSetting
                                settingKey="about_image_secondary"
                                title="Chi Siamo — Foto piccola"
                                hint="La foto quadrata sovrapposta in basso a destra."
                                value={settingsObj?.about_image_secondary}
                                testId="settings-about-secondary"
                            />
                        </div>

                        <div className="mt-8 max-w-xl border border-stone-900/10 bg-white p-6" data-testid="settings-instagram">
                            <h3 className="font-display text-lg font-medium text-stone-900">Instagram</h3>
                            <p className="mt-1 text-xs leading-relaxed text-stone-500">
                                Incolla l'indirizzo completo del vostro profilo Instagram: sul sito
                                comparirà il link "Seguici su Instagram" nei Contatti e nel footer.
                                Non serve nessuna password, il link è pubblico.
                            </p>
                            <div className="mt-4 flex gap-3">
                                <input
                                    data-testid="settings-instagram-input"
                                    className="w-full border border-stone-900/15 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-[#E75C15]"
                                    placeholder="https://instagram.com/santaluciaquattro"
                                    value={instagram}
                                    onChange={(e) => setInstagram(e.target.value)}
                                />
                                <button
                                    data-testid="settings-instagram-save"
                                    onClick={saveInstagram}
                                    disabled={savingIg}
                                    className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1C1917] px-6 py-3 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors hover:bg-[#B8430D] disabled:opacity-60"
                                >
                                    {savingIg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : tab === "testi" ? (
                    <div className="space-y-20">
                        <TextsForm settings={settingsObj} />
                        <HomeBlocksForm settings={settingsObj} />
                    </div>
                ) : (
                    <ServicesPageForm settings={settingsObj} />
                )}
                <ProjectForm
                    open={projectDialog}
                    onClose={() => setProjectDialog(false)}
                    project={editingProject}
                    onSaved={onProjectSaved}
                />
            </main>
        </div>
    );
}
